import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Input,
  Select,
  Tag,
  Modal,
  message,
  Row,
  Col,
  Tooltip,
  Typography,
  Statistic,
  Avatar,
  Badge,
  Empty,
  DatePicker,
  Tabs,
  Alert
} from 'antd';
import {
  ReloadOutlined,
  LogoutOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  DesktopOutlined,
  GlobalOutlined,
  StopOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';

import { UserService } from '../../../services/userService';
import type { UserSession, LoginAttempt, OnlineUserMetrics } from '../../../types/user';
import './index.css';

dayjs.extend(relativeTime);
dayjs.locale('zh-tw');

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface MonitoringState {
  sessions: UserSession[];
  loginAttempts: LoginAttempt[];
  metrics: OnlineUserMetrics;
  loading: boolean;
  sessionsLoading: boolean;
  attemptsLoading: boolean;
  metricsLoading: boolean;
  sessionsPagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  attemptsPagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  sessionFilters: {
    userId?: string;
  };
  attemptFilters: {
    userId?: string;
    result?: string;
    startDate?: string;
    endDate?: string;
  };
  selectedSessionRowKeys: React.Key[];
  activeTab: string;
  autoRefresh: boolean;
  refreshInterval: number;
}

const UserMonitoring: React.FC = () => {
  const [state, setState] = useState<MonitoringState>({
    sessions: [],
    loginAttempts: [],
    metrics: {
      totalOnline: 0,
      activeSessions: 0,
      newLoginsToday: 0,
      averageSessionTime: 0,
      topDepartments: [],
      sessionsByHour: []
    },
    loading: false,
    sessionsLoading: false,
    attemptsLoading: false,
    metricsLoading: false,
    sessionsPagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
    attemptsPagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
    sessionFilters: {},
    attemptFilters: {},
    selectedSessionRowKeys: [],
    activeTab: 'sessions',
    autoRefresh: true,
    refreshInterval: 30000, // 30 seconds
  });

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Auto refresh effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (state.autoRefresh) {
      interval = setInterval(() => {
        if (state.activeTab === 'sessions') {
          loadSessions();
          loadMetrics();
        } else if (state.activeTab === 'attempts') {
          loadLoginAttempts();
        }
      }, state.refreshInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [state.autoRefresh, state.refreshInterval, state.activeTab]);

  // Load all data
  const loadAllData = async () => {
    setState(prev => ({ ...prev, loading: true }));
    await Promise.all([
      loadSessions(),
      loadLoginAttempts(),
      loadMetrics()
    ]);
    setState(prev => ({ ...prev, loading: false }));
  };

  // Load active sessions
  const loadSessions = useCallback(async (params?: any) => {
    setState(prev => ({ ...prev, sessionsLoading: true }));
    
    try {
      const requestParams = {
        page: state.sessionsPagination.current,
        limit: state.sessionsPagination.pageSize,
        ...state.sessionFilters,
        ...params,
      };

      const response = await UserService.getActiveSessions(requestParams);
      
      setState(prev => ({
        ...prev,
        sessions: response.items || [],
        sessionsPagination: {
          current: response.pagination?.page || 1,
          pageSize: response.pagination?.limit || 20,
          total: response.pagination?.total || 0,
        },
        sessionsLoading: false,
      }));
    } catch (error) {
      message.error(`載入活躍會話失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ ...prev, sessionsLoading: false }));
    }
  }, [state.sessionsPagination.current, state.sessionsPagination.pageSize, state.sessionFilters]);

  // Load login attempts
  const loadLoginAttempts = useCallback(async (params?: any) => {
    setState(prev => ({ ...prev, attemptsLoading: true }));
    
    try {
      const requestParams = {
        page: state.attemptsPagination.current,
        limit: state.attemptsPagination.pageSize,
        ...state.attemptFilters,
        ...params,
      };

      const response = await UserService.getLoginAttempts(requestParams);
      
      setState(prev => ({
        ...prev,
        loginAttempts: response.items || [],
        attemptsPagination: {
          current: response.pagination?.page || 1,
          pageSize: response.pagination?.limit || 20,
          total: response.pagination?.total || 0,
        },
        attemptsLoading: false,
      }));
    } catch (error) {
      message.error(`載入登入記錄失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ ...prev, attemptsLoading: false }));
    }
  }, [state.attemptsPagination.current, state.attemptsPagination.pageSize, state.attemptFilters]);

  // Load metrics
  const loadMetrics = useCallback(async () => {
    setState(prev => ({ ...prev, metricsLoading: true }));
    
    try {
      const metrics = await UserService.getOnlineUserMetrics();
      setState(prev => ({
        ...prev,
        metrics,
        metricsLoading: false,
      }));
    } catch (error) {
      message.error(`載入監控指標失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ ...prev, metricsLoading: false }));
    }
  }, []);

  // Handle session search
  const handleSessionSearch = (value: string) => {
    const newFilters = {
      ...state.sessionFilters,
      userId: value,
    };
    setState(prev => ({
      ...prev,
      sessionFilters: newFilters,
      sessionsPagination: { ...prev.sessionsPagination, current: 1 },
    }));
    loadSessions({ ...newFilters, page: 1 });
  };

  // Handle attempt filters
  const handleAttemptFilterChange = (key: string, value: any) => {
    const newFilters = {
      ...state.attemptFilters,
      [key]: value,
    };
    setState(prev => ({
      ...prev,
      attemptFilters: newFilters,
      attemptsPagination: { ...prev.attemptsPagination, current: 1 },
    }));
    loadLoginAttempts({ ...newFilters, page: 1 });
  };

  // Handle date range change
  const handleDateRangeChange = (dates: any) => {
    const startDate = dates?.[0]?.format('YYYY-MM-DD');
    const endDate = dates?.[1]?.format('YYYY-MM-DD');
    handleAttemptFilterChange('startDate', startDate);
    handleAttemptFilterChange('endDate', endDate);
  };

  // Handle terminate session
  const handleTerminateSession = (session: UserSession) => {
    Modal.confirm({
      title: '終止會話',
      content: (
        <div>
          <p>確定要終止使用者 "{session.userName}" 的會話嗎？</p>
          <p style={{ color: '#8c8c8c', fontSize: '12px' }}>
            會話ID: {session.sessionId}
          </p>
          <p style={{ color: '#8c8c8c', fontSize: '12px' }}>
            登入時間: {dayjs(session.loginTime).format('YYYY-MM-DD HH:mm:ss')}
          </p>
        </div>
      ),
      okText: '確定終止',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await UserService.terminateSession(session.sessionId);
          message.success('會話已成功終止');
          loadSessions();
          loadMetrics();
        } catch (error) {
          message.error(`終止會話失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  // Handle force logout user
  const handleForceLogoutUser = (session: UserSession) => {
    Modal.confirm({
      title: '強制登出使用者',
      content: (
        <div>
          <p>確定要強制登出使用者 "{session.userName}" 的所有會話嗎？</p>
          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
            這將終止該使用者的所有活躍會話
          </p>
        </div>
      ),
      okText: '確定登出',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await UserService.forceLogoutUser(session.userId, '管理員強制登出');
          message.success('使用者已強制登出');
          loadSessions();
          loadMetrics();
        } catch (error) {
          message.error(`強制登出失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  // Handle batch terminate sessions
  const handleBatchTerminate = () => {
    if (state.selectedSessionRowKeys.length === 0) {
      message.warning('請選擇要終止的會話');
      return;
    }

    const selectedSessions = state.sessions.filter(s => state.selectedSessionRowKeys.includes(s.sessionId));
    
    Modal.confirm({
      title: '批量終止會話',
      content: `確定要終止選中的 ${selectedSessions.length} 個會話嗎？`,
      okText: '確定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          for (const session of selectedSessions) {
            await UserService.terminateSession(session.sessionId);
          }
          message.success(`成功終止 ${selectedSessions.length} 個會話`);
          setState(prev => ({ ...prev, selectedSessionRowKeys: [] }));
          loadSessions();
          loadMetrics();
        } catch (error) {
          message.error(`批量終止失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  // Handle table changes
  const handleSessionTableChange = (
    pagination: TablePaginationConfig,
    filters: any,
    sorter: any
  ) => {
    const newPagination = {
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 20,
      total: state.sessionsPagination.total,
    };

    setState(prev => ({ ...prev, sessionsPagination: newPagination }));
    
    loadSessions({
      page: newPagination.current,
      limit: newPagination.pageSize,
    });
  };

  const handleAttemptTableChange = (
    pagination: TablePaginationConfig,
    filters: any,
    sorter: any
  ) => {
    const newPagination = {
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 20,
      total: state.attemptsPagination.total,
    };

    setState(prev => ({ ...prev, attemptsPagination: newPagination }));
    
    loadLoginAttempts({
      page: newPagination.current,
      limit: newPagination.pageSize,
    });
  };

  // Handle row selection
  const handleSessionSelectChange = (selectedRowKeys: React.Key[]) => {
    setState(prev => ({ ...prev, selectedSessionRowKeys: selectedRowKeys }));
  };

  // Get device icon
  const getDeviceIcon = (userAgent: string): React.ReactNode => {
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return '📱';
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      return '📲';
    }
    return <DesktopOutlined />;
  };

  // Get session status
  const getSessionStatus = (session: UserSession): { status: any; text: string; color: string } => {
    const now = dayjs();
    const lastActivity = dayjs(session.lastActivityTime);
    const minutesInactive = now.diff(lastActivity, 'minute');

    if (session.forceLogout) {
      return { status: 'error', text: '已強制登出', color: '#ff4d4f' };
    } else if (minutesInactive > 30) {
      return { status: 'warning', text: '長時間未活動', color: '#fa8c16' };
    } else if (minutesInactive > 5) {
      return { status: 'processing', text: '閒置中', color: '#1890ff' };
    } else {
      return { status: 'success', text: '活躍', color: '#52c41a' };
    }
  };

  // Sessions table columns
  const sessionColumns: ColumnsType<UserSession> = [
    {
      title: '使用者資訊',
      key: 'user_info',
      width: 200,
      render: (_, session: UserSession) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar size={32} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }}>
            {session.userName?.charAt(0) || 'U'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>
              {session.userName}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {session.userId}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '會話狀態',
      key: 'status',
      width: 120,
      render: (_, session: UserSession) => {
        const status = getSessionStatus(session);
        return <Badge status={status.status} text={status.text} />;
      },
    },
    {
      title: '登入時間',
      dataIndex: 'loginTime',
      key: 'loginTime',
      width: 150,
      sorter: true,
      render: (time: string) => (
        <div>
          <div>{dayjs(time).format('MM-DD HH:mm')}</div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {dayjs(time).fromNow()}
          </Text>
        </div>
      ),
    },
    {
      title: '最後活動',
      dataIndex: 'lastActivityTime',
      key: 'lastActivityTime',
      width: 150,
      sorter: true,
      render: (time: string) => (
        <div>
          <div>{dayjs(time).format('MM-DD HH:mm')}</div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {dayjs(time).fromNow()}
          </Text>
        </div>
      ),
    },
    {
      title: '裝置與位置',
      key: 'device_location',
      width: 200,
      render: (_, session: UserSession) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
            {getDeviceIcon(session.userAgent)}
            <span style={{ fontSize: '12px' }}>
              {session.deviceInfo || '未知裝置'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <GlobalOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {session.ipAddress}
            </span>
          </div>
          {session.location && (
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
              📍 {session.location}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '部門',
      key: 'department',
      width: 120,
      render: (_, session: UserSession) => {
        if (!session.department) return <Text type="secondary">未分配</Text>;
        return (
          <Tag color="blue">
            {session.department.dptName}
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, session: UserSession) => (
        <Space size="small">
          <Tooltip title="會話詳情">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
            />
          </Tooltip>
          <Tooltip title="終止會話">
            <Button 
              type="text" 
              icon={<StopOutlined />} 
              size="small"
              danger
              onClick={() => handleTerminateSession(session)}
            />
          </Tooltip>
          <Tooltip title="強制登出">
            <Button 
              type="text" 
              icon={<LogoutOutlined />} 
              size="small"
              danger
              onClick={() => handleForceLogoutUser(session)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Login attempts table columns
  const attemptColumns: ColumnsType<LoginAttempt> = [
    {
      title: '使用者ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 120,
    },
    {
      title: '結果',
      dataIndex: 'result',
      key: 'result',
      width: 100,
      render: (result: string) => (
        <Tag 
          icon={result === 'SUCCESS' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          color={result === 'SUCCESS' ? 'success' : 'error'}
        >
          {result === 'SUCCESS' ? '成功' : '失敗'}
        </Tag>
      ),
    },
    {
      title: '時間',
      dataIndex: 'attemptTime',
      key: 'attemptTime',
      width: 150,
      sorter: true,
      render: (time: string) => (
        <div>
          <div>{dayjs(time).format('MM-DD HH:mm:ss')}</div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {dayjs(time).fromNow()}
          </Text>
        </div>
      ),
    },
    {
      title: 'IP位址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
    },
    {
      title: '失敗原因',
      dataIndex: 'failureReason',
      key: 'failureReason',
      width: 200,
      render: (reason: string) => (
        reason ? <Text type="danger">{reason}</Text> : '-'
      ),
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (location: string) => location || '-',
    },
  ];

  // Row selection for sessions
  const sessionRowSelection = {
    selectedRowKeys: state.selectedSessionRowKeys,
    onChange: handleSessionSelectChange,
  };

  return (
    <div className="user-monitoring-page">
      {/* Header Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="線上使用者"
              value={state.metrics.totalOnline}
              prefix={<UserOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
              loading={state.metricsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="活躍會話"
              value={state.metrics.activeSessions}
              prefix={<DesktopOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
              loading={state.metricsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="今日新登入"
              value={state.metrics.newLoginsToday}
              prefix={<ClockCircleOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
              loading={state.metricsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic
              title="平均會話時間"
              value={state.metrics.averageSessionTime}
              suffix="分鐘"
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
              loading={state.metricsLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Auto Refresh Control */}
      <Alert
        message={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              自動重新整理: {state.autoRefresh ? '開啟' : '關閉'} 
              {state.autoRefresh && ` (每 ${state.refreshInterval / 1000} 秒)`}
            </span>
            <Space>
              <Button
                size="small"
                type={state.autoRefresh ? 'primary' : 'default'}
                onClick={() => setState(prev => ({ ...prev, autoRefresh: !prev.autoRefresh }))}
              >
                {state.autoRefresh ? '關閉自動重新整理' : '開啟自動重新整理'}
              </Button>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={loadAllData}
                loading={state.loading}
              >
                立即重新整理
              </Button>
            </Space>
          </div>
        }
        type="info"
        style={{ marginBottom: '24px' }}
      />

      {/* Main Content */}
      <Card style={{ borderRadius: '8px' }}>
        <Title level={3} style={{ margin: 0, marginBottom: '24px' }}>
          使用者監控
        </Title>

        <Tabs 
          activeKey={state.activeTab} 
          onChange={(key) => setState(prev => ({ ...prev, activeTab: key }))}
          items={[
            {
              key: 'sessions',
              label: '線上會話',
              children: (
                <div>
                  {/* Sessions Filters */}
                  <div style={{ 
                    background: '#fafafa', 
                    padding: '16px', 
                    borderRadius: '6px',
                    marginBottom: '16px' 
                  }}>
                    <Row gutter={[16, 16]} align="middle">
                      <Col xs={24} md={8}>
                        <Search
                          placeholder="搜尋使用者ID"
                          allowClear
                          onSearch={handleSessionSearch}
                          prefix={<SearchOutlined />}
                        />
                      </Col>
                      <Col xs={24} md={16}>
                        <Space>
                          <Text>共 {state.sessionsPagination.total} 個活躍會話</Text>
                        </Space>
                      </Col>
                    </Row>
                  </div>

                  {/* Batch Actions */}
                  {state.selectedSessionRowKeys.length > 0 && (
                    <div style={{ 
                      marginBottom: '16px',
                      padding: '12px 16px',
                      background: '#fff2e8',
                      borderRadius: '6px',
                      border: '1px solid #ffbb96'
                    }}>
                      <Space>
                        <Text strong>已選擇 {state.selectedSessionRowKeys.length} 個會話</Text>
                        <Button 
                          danger 
                          size="small"
                          icon={<StopOutlined />}
                          onClick={handleBatchTerminate}
                        >
                          批量終止
                        </Button>
                      </Space>
                    </div>
                  )}

                  {/* Sessions Table */}
                  <Table
                    columns={sessionColumns}
                    dataSource={state.sessions}
                    rowKey="sessionId"
                    loading={state.sessionsLoading}
                    pagination={{
                      current: state.sessionsPagination.current,
                      pageSize: state.sessionsPagination.pageSize,
                      total: state.sessionsPagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => 
                        `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
                      pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                    rowSelection={sessionRowSelection}
                    onChange={handleSessionTableChange}
                    scroll={{ x: 'max-content' }}
                    locale={{
                      emptyText: (
                        <Empty 
                          description="暫無活躍會話" 
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              )
            },
            {
              key: 'attempts',
              label: '登入記錄',
              children: (
                <div>
                  {/* Attempts Filters */}
                  <div style={{ 
                    background: '#fafafa', 
                    padding: '16px', 
                    borderRadius: '6px',
                    marginBottom: '16px' 
                  }}>
                    <Row gutter={[16, 16]} align="middle">
                      <Col xs={24} md={6}>
                        <Input
                          placeholder="搜尋使用者ID"
                          allowClear
                          prefix={<SearchOutlined />}
                          onChange={(e) => handleAttemptFilterChange('userId', e.target.value)}
                        />
                      </Col>
                      <Col xs={24} md={4}>
                        <Select
                          placeholder="結果"
                          allowClear
                          style={{ width: '100%' }}
                          onChange={(value) => handleAttemptFilterChange('result', value)}
                        >
                          <Option value="SUCCESS">成功</Option>
                          <Option value="FAILED">失敗</Option>
                        </Select>
                      </Col>
                      <Col xs={24} md={8}>
                        <RangePicker
                          style={{ width: '100%' }}
                          placeholder={['開始日期', '結束日期']}
                          onChange={handleDateRangeChange}
                        />
                      </Col>
                      <Col xs={24} md={6}>
                        <Space>
                          <Text>共 {state.attemptsPagination.total} 筆記錄</Text>
                        </Space>
                      </Col>
                    </Row>
                  </div>

                  {/* Login Attempts Table */}
                  <Table
                    columns={attemptColumns}
                    dataSource={state.loginAttempts}
                    rowKey="attemptId"
                    loading={state.attemptsLoading}
                    pagination={{
                      current: state.attemptsPagination.current,
                      pageSize: state.attemptsPagination.pageSize,
                      total: state.attemptsPagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => 
                        `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
                      pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                    onChange={handleAttemptTableChange}
                    scroll={{ x: 'max-content' }}
                    locale={{
                      emptyText: (
                        <Empty 
                          description="暫無登入記錄" 
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default UserMonitoring;